import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Checkbox,
  Chip,
  CircularProgress,
  Alert,
  InputAdornment,
  Paper,
  Divider
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import LibraryAddIcon from '@mui/icons-material/LibraryAdd';
import {
  PoolProblem,
  ProblemCategory,
  ProblemTag,
  getPoolProblems,
  getCategories,
  getTags,
  bulkAssignPoolProblems
} from '../../../../services/problemPoolService';
import { createTextPreview } from 'utils/textUtils';
import { normalizeImagePath } from 'utils/imageUtils';

interface ProblemPickerDialogProps {
  open: boolean;
  onClose: () => void;
  sessionId: number;
  groupId: number;
  onSuccess: () => void;
}

const difficultyStars = (level: number): string => {
  return '★'.repeat(level) + '☆'.repeat(5 - level);
};

const ProblemPickerDialog: React.FC<ProblemPickerDialogProps> = ({
  open,
  onClose,
  sessionId,
  groupId,
  onSuccess
}) => {
  const [problems, setProblems] = useState<PoolProblem[]>([]);
  const [categories, setCategories] = useState<ProblemCategory[]>([]);
  const [, setTags] = useState<ProblemTag[]>([]);
  const [selectedProblemIds, setSelectedProblemIds] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filter state
  const [search, setSearch] = useState('');
  const [categoryId, setCategoryId] = useState<number | ''>('');
  const [difficultyLevel, setDifficultyLevel] = useState<number | ''>('');

  // Fetch initial data
  useEffect(() => {
    if (open) {
      fetchData();
    }
  }, [open]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [problemsData, categoriesData, tagsData] = await Promise.all([
        getPoolProblems({ limit: 100, sortBy: 'created_at', sortOrder: 'desc' }),
        getCategories(),
        getTags()
      ]);
      setProblems(problemsData.problems);
      setCategories(categoriesData);
      setTags(tagsData);
    } catch (err) {
      setError('Failed to load problems');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Filter problems locally for quick filtering
  const filteredProblems = useMemo(() => {
    return problems.filter(problem => {
      // Search filter
      if (search) {
        const searchLower = search.toLowerCase();
        const matchesSearch =
          problem.problemStatement.toLowerCase().includes(searchLower) ||
          (problem.title && problem.title.toLowerCase().includes(searchLower));
        if (!matchesSearch) return false;
      }

      // Category filter
      if (categoryId && problem.categoryId !== categoryId) {
        return false;
      }

      // Difficulty filter
      if (difficultyLevel && problem.difficultyLevel !== difficultyLevel) {
        return false;
      }

      return true;
    });
  }, [problems, search, categoryId, difficultyLevel]);

  const handleToggleProblem = (problemId: number) => {
    setSelectedProblemIds(prev =>
      prev.includes(problemId)
        ? prev.filter(id => id !== problemId)
        : [...prev, problemId]
    );
  };

  const handleSelectAll = () => {
    if (selectedProblemIds.length === filteredProblems.length) {
      setSelectedProblemIds([]);
    } else {
      setSelectedProblemIds(filteredProblems.map(p => p.id));
    }
  };

  const handleSubmit = async () => {
    if (selectedProblemIds.length === 0) return;

    setSubmitting(true);
    setError(null);
    try {
      await bulkAssignPoolProblems(selectedProblemIds, sessionId, groupId);
      setSelectedProblemIds([]);
      onSuccess();
      onClose();
    } catch (err) {
      const error = err as { response?: { data?: { message?: string } }; message?: string };
      setError(error.response?.data?.message || error.message || 'Failed to add problems');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelectedProblemIds([]);
    setSearch('');
    setCategoryId('');
    setDifficultyLevel('');
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <LibraryAddIcon color="primary" />
        Add Problems from Pool
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Filters */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Search problems..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
            sx={{ minWidth: 200, flex: 1 }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Category</InputLabel>
            <Select
              value={categoryId}
              label="Category"
              onChange={(e) => setCategoryId(e.target.value as number | '')}
            >
              <MenuItem value="">All</MenuItem>
              {categories.map(cat => (
                <MenuItem key={cat.id} value={cat.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        bgcolor: cat.color
                      }}
                    />
                    {cat.name}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>Difficulty</InputLabel>
            <Select
              value={difficultyLevel}
              label="Difficulty"
              onChange={(e) => setDifficultyLevel(e.target.value as number | '')}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value={1}>★ Easy</MenuItem>
              <MenuItem value={2}>★★ Medium</MenuItem>
              <MenuItem value={3}>★★★ Challenging</MenuItem>
              <MenuItem value={4}>★★★★ Hard</MenuItem>
              <MenuItem value={5}>★★★★★ Expert</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Selection controls */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Button
            size="small"
            onClick={handleSelectAll}
            disabled={loading || filteredProblems.length === 0}
          >
            {selectedProblemIds.length === filteredProblems.length && filteredProblems.length > 0
              ? 'Deselect All'
              : 'Select All'}
          </Button>
          <Typography variant="body2" color="text.secondary">
            {filteredProblems.length} problems found • {selectedProblemIds.length} selected
          </Typography>
        </Box>

        {/* Problems list */}
        <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : filteredProblems.length === 0 ? (
            <Box sx={{ textAlign: 'center', p: 4 }}>
              <Typography color="text.secondary">
                {problems.length === 0
                  ? 'No problems in the pool yet. Create some in the Problem Pool page.'
                  : 'No problems match your filters.'}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {filteredProblems.map(problem => (
                <Paper
                  key={problem.id}
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    cursor: 'pointer',
                    bgcolor: selectedProblemIds.includes(problem.id)
                      ? 'action.selected'
                      : 'background.paper',
                    '&:hover': {
                      bgcolor: selectedProblemIds.includes(problem.id)
                        ? 'action.selected'
                        : 'action.hover'
                    }
                  }}
                  onClick={() => handleToggleProblem(problem.id)}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <Checkbox
                      checked={selectedProblemIds.includes(problem.id)}
                      size="small"
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => handleToggleProblem(problem.id)}
                    />
                    {problem.imagePath && (
                      <Box
                        component="img"
                        src={normalizeImagePath(problem.imagePath)}
                        alt="Problem"
                        sx={{
                          width: 48,
                          height: 48,
                          objectFit: 'cover',
                          borderRadius: 1,
                          border: '1px solid',
                          borderColor: 'divider',
                          flexShrink: 0
                        }}
                      />
                    )}
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        {problem.title && (
                          <Typography variant="subtitle2" fontWeight="medium">
                            {problem.title}
                          </Typography>
                        )}
                        {problem.categoryName && (
                          <Chip
                            label={problem.categoryName}
                            size="small"
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        )}
                        <Chip
                          label={problem.problemType === 'multiple_choice' ? 'MC' : 'Open'}
                          size="small"
                          variant="outlined"
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {difficultyStars(problem.difficultyLevel)}
                        </Typography>
                      </Box>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical'
                        }}
                      >
                        {createTextPreview(problem.problemStatement, 140)}
                      </Typography>
                      {problem.tags && problem.tags.length > 0 && (
                        <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                          {problem.tags.map(tag => (
                            <Chip
                              key={tag.id}
                              label={tag.name}
                              size="small"
                              sx={{
                                bgcolor: tag.color,
                                color: 'white',
                                height: 18,
                                '& .MuiChip-label': { px: 0.75, fontSize: '0.65rem' }
                              }}
                            />
                          ))}
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Paper>
              ))}
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1, pl: 2 }}>
          {selectedProblemIds.length > 0 &&
            `${selectedProblemIds.length} problem${selectedProblemIds.length > 1 ? 's' : ''} will be cloned into this group`}
        </Typography>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || selectedProblemIds.length === 0}
          startIcon={submitting ? <CircularProgress size={20} /> : <LibraryAddIcon />}
        >
          Add {selectedProblemIds.length > 0 ? `(${selectedProblemIds.length})` : ''}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProblemPickerDialog;
